import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DashboardApi } from '../../../apis/DashboardApi';
import configs from '../../../configs';

interface SubscriptionBannerProps {
  onView?: () => void;
}

/**
 * A banner component to display at the top of pages to inform
 * users about their subscription status
 */
const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({ onView }) => {
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const navigate = useNavigate();
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const isLifetime = userLoginData?.data?.user_plan?.is_lifetime === true;

  useEffect(() => {
    // No subscriptions to show while payments are off (single free 1 TB tier).
    if (configs.paymentsEnabled) {
      fetchSubscriptionData();
    }
  }, []);

  useEffect(() => {
    // Calculate days left in grace period
    if (subscription?.expiration_notified_at) {
      const notifiedDate = new Date(subscription.expiration_notified_at);
      const suspensionDate = new Date(notifiedDate);
      suspensionDate.setDate(suspensionDate.getDate() + 10); // 10 day grace period
      
      const today = new Date();
      const timeLeft = suspensionDate.getTime() - today.getTime();
      const daysRemaining = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
      
      setDaysLeft(daysRemaining > 0 ? daysRemaining : 0);
    }
  }, [subscription]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response:any = await DashboardApi.getUserSubscription();
      if (response?.data?.status === 'success') {
        setSubscription(response.data.data.subscription);
        
        // Notify parent component that banner was viewed
        if (onView && response.data.data.subscription) {
          onView();
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setLoading(false);
    }
  };

  // No banner while payments are off (single free 1 TB tier).
  if (!configs.paymentsEnabled) {
    return null;
  }

  // Lifetime users never see expiration/payment banners
  if (isLifetime) {
    return null;
  }

  if (loading || !subscription) {
    return null;
  }

  if (subscription.is_lifetime) {
    return null;
  }

  // Don't show banner for active subscriptions with auto-renew enabled
  if (subscription.status === 'active' && subscription.auto_renew && !subscription.expiration_notified_at) {
    return null;
  }

  // For cancelled subscriptions
  if (subscription.status === 'cancelled') {
    return (
      <div className="bg-red-50 border-b border-red-100">
        <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap">
            <div className="flex-1 flex items-center">
              <AlertTriangle className="flex-shrink-0 h-5 w-5 text-red-600" />
              <p className="ml-3 font-medium text-red-700 truncate">
                <span>Your subscription has been cancelled and will expire on {new Date(subscription.end_date).toLocaleDateString()}</span>
              </p>
            </div>
            <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
              <button
                onClick={() => navigate('/upgrade')}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Renew Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For subscriptions in grace period
  if (subscription.status === 'active' && subscription.expiration_notified_at) {
    return (
      <div className="bg-amber-50 border-b border-amber-100">
        <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap">
            <div className="flex-1 flex items-center">
              <Clock className="flex-shrink-0 h-5 w-5 text-amber-600" />
              <p className="ml-3 font-medium text-amber-700 truncate">
                <span>Your subscription has expired. {daysLeft && daysLeft > 0 ? `${daysLeft} days left` : 'Limited time'} before account suspension.</span>
              </p>
            </div>
            <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
              <button
                onClick={() => navigate('/upgrade')}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700"
              >
                Renew Subscription
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For active subscriptions with auto-renew disabled
  if (subscription.status === 'active' && !subscription.auto_renew) {
    return (
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap">
            <div className="flex-1 flex items-center">
              <Clock className="flex-shrink-0 h-5 w-5 text-blue-600" />
              <p className="ml-3 font-medium text-blue-700 truncate">
                <span>Auto-renewal is disabled. Your subscription will expire on {new Date(subscription.end_date).toLocaleDateString()}</span>
              </p>
            </div>
            <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
              <button
                onClick={() => navigate('/user-settings')}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Enable Auto-Renewal
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;